import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Alert, Container } from '@mui/material';
import { AxiosResponse } from 'axios';
import { CreateBookingSlot } from '../types/bookingSlot';
import { BarberServices } from '../types/barber';
import { Booking } from '../types/booking';
import { getBarberSlotsByBarberId } from '../middleware/reservation_service/getRequests';
import { getBarberById } from '../middleware/registration_service/getRequests';
import { createBooking } from '../middleware/reservation_service/createRequest';
import PublicBookingCalendar, {
  BookingFormData,
} from './components/PublicBookingCalendar';

const BarberBookingCalendar: React.FC = () => {
  const { barberId: barberIdParam } = useParams<{ barberId: string }>();
  const barberId = (() => {
    if (!barberIdParam) return null;
    try {
      return BigInt(barberIdParam);
    } catch {
      return null;
    }
  })();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [barberName, setBarberName] = useState('');
  const [barberEmail, setBarberEmail] = useState('');
  const [barberServices, setBarberServices] = useState<BarberServices[]>([]);
  const [slots, setSlots] = useState<CreateBookingSlot[]>([]);

  const loadBarberData = useCallback(async () => {
    if (!barberId) return;

    try {
      const response = await getBarberById(barberId);
      if (response.data) {
        setBarberName(
          `${response.data.firstName || ''} ${
            response.data.lastName || ''
          }`.trim()
        );
        setBarberEmail(response.data.email || '');
        setBarberServices(response.data.barberServices || []);
      }
    } catch (err) {
      console.error('Failed to load barber data:', err);
      setError('Unable to load barber details. Please try again later.');
    }
  }, [barberId]);

  const loadSlots = useCallback(async () => {
    if (!barberId) return;

    try {
      setLoading(true);
      const response: AxiosResponse = await getBarberSlotsByBarberId(barberId);
      const rawSlots = Array.isArray(response.data) ? response.data : [];

      const availableSlots: CreateBookingSlot[] = rawSlots
        .filter((slot: any) => slot.status === 'AVAILABLE')
        .map((slot: any) => ({
          id: slot.id,
          barber_id: BigInt(slot.barber_id),
          service_id: BigInt(slot.service_id),
          start_time: new Date(`${slot.slot_date}T${slot.start_time}:00`),
          status: slot.status,
          slot_date: new Date(slot.slot_date),
        }));

      setSlots(availableSlots);
    } catch (err) {
      console.error('Failed to load barber slots:', err);
      setError('Unable to load available slots. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [barberId]);

  useEffect(() => {
    if (!barberId) {
      setError('Invalid booking link.');
      return;
    }
    loadBarberData();
    loadSlots();
  }, [barberId, loadBarberData, loadSlots]);

  const getService = (serviceId: bigint) =>
    barberServices.find((s) => BigInt(s.id || 0) === serviceId);

  const handleBook = async (
    slot: CreateBookingSlot,
    formData: BookingFormData
  ) => {
    const service = getService(slot.service_id);
    if (!service) {
      throw new Error('Unable to retrieve complete slot information.');
    }

    const reservationData: Booking = {
      slot_id: slot.id,
      customer_email: formData.customerEmail,
      customer_phone: formData.customerPhone,
      customer_name: formData.customerName,
      barber_name: barberName,
      barber_email_address: barberEmail,
      service_name: service.name,
      price: service.price,
      status: 'PENDING',
      type: 'BARBER',
      barber_id: slot.barber_id,
    };

    await createBooking(reservationData);
    setSlots((prev) => prev.filter((s) => s.id !== slot.id));
  };

  if (!barberId) {
    return (
      <Container sx={{ marginTop: '75px !important' }}>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Invalid booking link.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <PublicBookingCalendar
      title={barberName ? `Book with ${barberName}` : "Barber's Calendar"}
      subtitle="Select a date and time to create your reservation"
      loading={loading}
      error={error}
      slots={slots}
      showBarberName={false}
      getBarberName={() => barberName}
      getService={getService}
      onBook={handleBook}
    />
  );
};

export default BarberBookingCalendar;
